<template>
  <div class="content-wrapper">
    <section class="content-header">
      <h1>
        Promotions
      </h1>
      <ol class="breadcrumb">
        <li>
          <a @click="$router.push({ name: 'dashboard' })">
            <i class="fa fa-dashboard"></i> Dashboard
          </a>
        </li>
        <li>
          <a @click="$router.push({ name: 'promo-index' })">
            <i class="fa fa-gift"></i> Promotions
          </a>
        </li>
        <li>
          <a @click="$router.push({ name: 'promo-config-index' })">
            <i class="fa fa-credit-card"></i> Payments
          </a>
        </li>
      </ol>
    </section>

    <section class="content">
      <div class="box">
        <div class="box-header with-border">
          <strong>Filter Promo Payments</strong>
          <div class="box-tools pull-right">
            <button type="button"
              class="btn btn-box-tool"
              data-widget="collapse">
              <i class="fa fa-minus"></i>
            </button>
          </div>
        </div>

        <div class="box-body" style="overflow: inherit">
          <div class="row">
            <div class="col-12 col-sm-4">
              <div class="form-group">
                <label for="promoPaymentName">
                  Name
                </label>

                <input type="text"
                  class="form-control"
                  id="promoPaymentName"
                  placeholder="Search by name here"
                  v-model="params.name"
                  @keyup="onInput">
              </div>
            </div>

            <div class="col-12 col-sm-8">
              <div class="form-group">
                <label for="paymentMethod">
                  Payment Method
                </label>

                <v-select multiple
                  id="paymentMethod"
                  name="paymentMethod"
                  v-model="paymentsSelected"
                  :options="payments">
                </v-select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="box">
        <div class="box-header">
          <div class="box-title">
            <h3>Promo Payments</h3>
          </div>
          <a class="btn btn-success pull-right"
            @click.prevent="add">
            Create New
          </a>
        </div>

        <div class="box-body table-responsive">
          <table class="table table-hover">
            <transition name="fade" mode="out-in">
              <tbody v-if="isLoading" key="loading">
                <tr>
                  <td colspan="6">
                    <strong>Loading</strong>
                  </td>
                </tr>
              </tbody>

              <tbody v-else-if="!isLoading && items.length < 1" key="empty">
                <tr>
                  <td colspan="6">
                    <strong>Result not found</strong>
                  </td>
                </tr>
              </tbody>

              <tbody v-else key="data">
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Days</th>
                  <th>Payment Methods</th>
                  <th class="text-center">Status</th>
                  <th>Action</th>
                </tr>

                <tr v-for="(item, index) in items" :key="index">
                  <td>{{ item.name }}</td>
                  <td>
                    <span v-for="promoType in item.type" :key="promoType.id">
                      {{ promoType }}
                    </span>
                  </td>

                  <td>
                    <span v-for="day in item.days" :key="day">
                      {{ parseDay(day) }}
                    </span>
                  </td>

                  <td>
                    <ul>
                      <li v-for="method in item.payment_method" :key="method.id">
                        {{ method.name }}
                      </li>
                    </ul>
                  </td>

                  <td class="text-center">
                    <strong :class="{
                      'text-success': item.is_enabled === 1,
                      'text-muted': item.is_enabled === 0
                    }">
                      <i class="fa fa-check-square-o" v-if="item.is_enabled === 1"></i>
                      <i class="fa fa-square-o" v-else></i>
                      {{ parseStatus(item.is_enabled) }}
                    </strong>
                    <a class="btn btn-block btn-default btn-xs"
                      @click.prevent="toggleStatus(item)">
                      <template v-if="item.is_enabled === 1">Deactivate</template>
                      <template v-else>Activate</template>
                    </a>
                  </td>

                  <td>
                    <a class="btn btn-block btn-info btn-xs"
                      @click.prevent="view(item.id)">
                      View
                    </a>
                    <a class="btn btn-block btn-primary btn-xs"
                      @click.prevent="edit(item.id)">
                      Edit
                    </a>
                    <a class="btn btn-block btn-danger btn-xs"
                      @click.prevent="remove(item)">
                      Delete
                    </a>
                  </td>
                </tr>
              </tbody>
            </transition>
          </table>
        </div>

        <div class="box-footer">
          <Pagination
            :pagination="pagination"
            @goto-page="changePage" />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import API_BASEURL from '@/config';
import debounce from 'lodash/debounce';
import Common from '@/mixins/Common';
import Pagination from '@/components/modules/common/Pagination';

export default {
  name: 'PromoConfigIndex',

  mixins: [Common],

  components: {
    Pagination,
  },

  data() {
    return {
      isLoading: true,
      items: [],
      pagination: {},

      params: {
        name: null,
        payment_method: null,
        page: null,
      },

      payments: [],
      paymentsSelected: null,
      isInitial: true,
    };
  },

  computed: {
    hasQuery() {
      const query = Object.keys(this.params).filter(key => this.params[key] !== null);
      return (query.length > 0);
    },
  },

  watch: {
    paymentsSelected() {
      if (!this.isInitial) {
        this.setMethodAndFetch();
      }
    },
  },

  methods: {
    async init() {
      await this.setQuery();
      await this.getPaymentMethod();
      await this.fetch();
      this.isInitial = false;
    },

    fetch(page) {
      this.params.page = page || null;

      return new Promise((resolve, reject) => {
        this.axios.get('/promoconfigs', { params: this.params })
          .then((response) => {
            const { data, meta } = response.data;
            const isEmpty = (!data && (meta.pagination.current_page > meta.pagination.total_pages));

            this.items = (!isEmpty) ? data : [];
            this.pagination = (!isEmpty) ? meta.pagination : {};

            this.setPushState();
            resolve();
          })
          .catch(() => {
            this.$swal({
              title: 'Fetch Failed',
              text: 'Failed to fetch Promo Config data',
              type: 'warning',
              timer: 5000,
            });

            reject();
          })
          .finally(() => {
            this.isLoading = false;
          });
      });
    },

    setQuery() {
      return new Promise((resolve) => {
        this.params.name = this.$route.query.name || null;
        this.params.payment_method = this.$route.query.payment_method || null;
        this.params.page = (this.$route.query.page) ? this.$route.query.page : null;

        resolve();
      });
    },

    generateQuery(params) {
      if (!params) return null;

      const query = Object.keys(params)
        .filter(key => (params[key]))
        .map(key => `${key}=${this.params[key]}`);

      return (query) ? `?${query.join('&')}` : null;
    },

    setPushState() {
      const title = `Promo configs search page ${this.params.page}`;
      const paramUrl = '/promo/config';
      const parameters = this.generateQuery(this.params);
      window.history.pushState(title, null, `${paramUrl}${parameters}`);
    },

    onInput: debounce(function debouncedInput() {
      this.fetch();
    }, 500),

    getPaymentMethod() {
      const config = {
        baseURL: API_BASEURL,
        params: {
          page: 1,
          perpage: 100,
        },
      };

      const mapToSelected = (list, methods) => {
        if (!methods || !list) return null;

        const selected = methods.split(',');
        if (!selected && selected.length < 1) return null;

        return list.filter(iL => selected.some(iM => parseInt(iM, 10) === iL.value));
      };

      return new Promise((resolve, reject) => {
        this.axios.get('/payments/v2/payment-method', config)
          .then((response) => {
            const { data } = response.data;
            this.payments = data.map(item => ({ label: item.name, value: item.id }));
            this.paymentsSelected = mapToSelected(this.payments, this.params.payment_method);
            resolve();
          })
          .catch((error) => {
            this.$swal({
              title: 'Fetch Failed',
              text: 'Failed to fetch payment method data',
              type: 'warning',
              timer: 5000,
            });
            reject(new Error(error));
          });
      });
    },

    setMethodParam() {
      return new Promise((resolve) => {
        const methodParam = [];

        if (this.paymentsSelected.length > 0) {
          this.paymentsSelected.forEach((item) => {
            methodParam.push(item.value);
          });
        }

        this.params.payment_method = methodParam.toString();

        resolve();
      });
    },

    async setMethodAndFetch() {
      await this.setMethodParam();
      await this.fetch();
    },

    changePage(page) {
      this.fetch(page);
    },

    parseDay(day) {
      if (day === 1) return 'Mon';
      if (day === 2) return 'Tue';
      if (day === 3) return 'Wed';
      if (day === 4) return 'Thu';
      if (day === 5) return 'Fri';
      if (day === 6) return 'Sat';
      if (day === 7) return 'Sun';
      return '';
    },

    parseStatus(status) {
      if (status === 1) return 'Active';
      return 'Inactive';
    },

    add() {
      this.$router.push({ name: 'promo-config-create' });
    },

    view(id) {
      this.$router.push({
        name: 'promo-config-view',
        params: { id },
      });
    },

    edit(id) {
      this.$router.push({
        name: 'promo-config-edit',
        params: { id },
      });
    },

    toggleStatus(item) {
      const params = (item.is_enabled === 1) ? `deactivate/${item.id}` : `activate/${item.id}`;
      this.axios.patch(`/promoconfigs/${params}`).then(() => this.fetch());
    },

    delete(id) {
      return new Promise((resolve, reject) => {
        this.axios.delete(`/promoconfigs/${id}`)
          .then((response) => {
            console.log(response);
            resolve();
          })
          .catch((error) => {
            console.log(error);
            reject();
          });
      });
    },

    remove(item) {
      return this.$swal({
        title: 'Delete Config',
        text: `Are you sure to delete ${item.name}`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then((result) => {
        if (result.value) {
          (async () => {
            try {
              await this.delete(item.id);
              await this.fetch();
              this.$swal({
                title: 'Deleted!',
                text: `${item.name} has been deleted.`,
                type: 'success',
                timer: 5000,
              });
            } catch (error) {
              console.log(error);
            }
          })();
        }
      });
    },
  },

  created() {
    this.init();
  },
};
</script>



// WEBPACK FOOTER //
// src/components/modules/promo/config/Index.vue